import { CustomerBudgetMap, monthToNumber, ProcessedBudget, ForecastData } from './types';

export function parseDuration(durationString: string): number {
  const match = durationString.match(/(\d+)/);
  return match ? parseInt(match[0], 10) : 3;
}

export function parseProjectBudget(budgetString: string): number {
  return parseFloat(budgetString.replace(/[^0-9.-]+/g, ''));
}

export function calculateMonthlyBudget(totalBudget: number, durationMonths: number): number {
  return totalBudget / durationMonths;
}

export function initializeBudgetMap(customers: any[], years: string[]): Map<string, CustomerBudgetMap> {
  const budgetMap = new Map<string, CustomerBudgetMap>();
  
  customers.forEach(customer => {
    budgetMap.set(customer.name, {
      color: customer.color,
      total: 0,
      years: Object.fromEntries(years.map(year => [year, 0])),
      projects: Object.fromEntries(years.map(year => [year, []]))
    });
  });

  return budgetMap;
}

export function processBudgetData(
  projects: any[],
  budgetMap: Map<string, CustomerBudgetMap>,
  years: string[]
): Map<string, ForecastData> {
  const forecastMap = new Map<string, ForecastData>();
  
  // Initialize forecast map
  years.forEach(year => {
    forecastMap.set(year, {
      confirmed: 0,
      projected: 0,
      weighted: 0,
      minimum: 0
    });
  });

  projects.forEach(project => {
    if (!project.customer || !project.budget || !project.year || !project.month || !project.duration) return;

    const budget = parseProjectBudget(project.budget);
    if (isNaN(budget)) return;

    const customerName = project.customer.name;
    const startYear = parseInt(project.year);
    const startMonth = monthToNumber[project.month];
    const durationMonths = parseDuration(project.duration.name);
    const monthlyBudget = calculateMonthlyBudget(budget, durationMonths);

    const customer = budgetMap.get(customerName)!;
    customer.total += budget;

    let remainingMonths = durationMonths;
    let currentYear = startYear;
    let currentMonth = startMonth;

    while (remainingMonths > 0) {
      const yearStr = currentYear.toString();
      if (years.includes(yearStr)) {
        const monthsInYear = Math.min(remainingMonths, 12 - currentMonth);
        const yearContribution = monthlyBudget * monthsInYear;
        
        customer.years[yearStr] = (customer.years[yearStr] || 0) + yearContribution;
        customer.projects[yearStr].push({
          id: project.id,
          name: project.project_name,
          monthlyBudget,
          months: monthsInYear,
          totalContribution: yearContribution
        });

        // Update forecast data
        const yearData = forecastMap.get(yearStr)!;
        yearData.confirmed += yearContribution;
        forecastMap.set(yearStr, yearData);
      }
      remainingMonths -= (12 - currentMonth);
      currentYear++;
      currentMonth = 0;
    }
  });

  return forecastMap;
}

export function processProjections(
  projections: any[],
  forecastMap: Map<string, ForecastData>,
  years: string[]
): void {
  projections.forEach(projection => {
    const value = parseProjectBudget(projection.value);
    if (isNaN(value)) return;

    const yearStr = projection.year;
    if (years.includes(yearStr)) {
      const yearData = forecastMap.get(yearStr)!;
      yearData.projected += value;
      yearData.weighted += (value * projection.confidence / 100);
      yearData.minimum += (value * 0.5); // Minimum is set to 50% of projected value
      forecastMap.set(yearStr, yearData);
    }
  });
}

export function formatProcessedBudgets(budgetMap: Map<string, CustomerBudgetMap>): ProcessedBudget[] {
  return Array.from(budgetMap.entries())
    .map(([customerName, data]) => ({
      customerName,
      color: data.color,
      totalBudget: data.total,
      projects: data.projects,
      ...Object.fromEntries(
        Object.entries(data.years).map(([year, amount]) => [year, Math.round(amount)])
      )
    }))
    .sort((a, b) => b.totalBudget - a.totalBudget);
}